import React, { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import {
    Shield, Database, Brain, Activity, RefreshCw, Users, Target, Wifi,
    Settings, Key, Plus, Pencil, Trash2, CheckCircle, XCircle, Eye, EyeOff,
    AlertTriangle, Clock, Server, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';

const Tab = ({ active, onClick, icon: Icon, label, testId }) => (
    <button data-testid={testId} onClick={onClick}
        className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${active ? 'bg-white/10 text-white border border-white/10' : 'text-white/40 hover:text-white/70 hover:bg-white/5'}`}>
        <Icon className="w-4 h-4" />
        <span className="hidden md:inline">{label}</span>
    </button>
);

const StatusDot = ({ status }) => {
    const c = status === 'connected' || status === 'active' ? 'bg-green-400' : status === 'idle' ? 'bg-yellow-400' : status === 'running' ? 'bg-blue-400 animate-pulse' : 'bg-red-400';
    return <div className={`w-2.5 h-2.5 rounded-full ${c}`} />;
};

export default function AdminPanel() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('overview');
    const [users, setUsers] = useState([]);
    const [apiConfig, setApiConfig] = useState(null);
    const [apiLogs, setApiLogs] = useState([]);
    const [connectivity, setConnectivity] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [showUserModal, setShowUserModal] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'user' });
    const [showApiKeyModal, setShowApiKeyModal] = useState(false);
    const [apiKeyForm, setApiKeyForm] = useState({ sportmonks_api_token: '', emergent_llm_key: '' });
    const [showPw, setShowPw] = useState({});
    const [actionMsg, setActionMsg] = useState('');

    const [historicalSyncing, setHistoricalSyncing] = useState(false);
    const [historicalStatus, setHistoricalStatus] = useState(null);
    const [aiTraining, setAiTraining] = useState(false);
    const [matchReadiness, setMatchReadiness] = useState(null);
    const [batchRunning, setBatchRunning] = useState(false);
    const [pushingLive, setPushingLive] = useState(false);
    const [setupRunning, setSetupRunning] = useState(false);
    const [upcomingPreds, setUpcomingPreds] = useState(null);

    const flash = (msg) => { setActionMsg(msg); setTimeout(() => setActionMsg(''), 3000); };

    const loadStats = useCallback(() => {
        API.get('/admin/stats').then(r => { setStats(r.data); setLoading(false); }).catch(() => setLoading(false));
    }, []);

    useEffect(() => { loadStats(); }, [loadStats]);

    useEffect(() => {
        if (tab === 'users') API.get('/admin/users').then(r => setUsers(r.data.users || [])).catch(() => {});
        if (tab === 'api') {
            API.get('/admin/api-config').then(r => setApiConfig(r.data)).catch(() => {});
            API.get('/admin/api-logs').then(r => setApiLogs(r.data.logs || [])).catch(() => {});
        }
        if (tab === 'connectivity') {
            setConnectivity(null);
            API.get('/admin/connectivity').then(r => setConnectivity(r.data)).catch(() => setConnectivity({ error: true }));
        }
        if (tab === 'readiness') {
            API.get('/admin/upcoming-predictions').then(r => setUpcomingPreds(r.data)).catch(() => {});
        }
    }, [tab]);

    const triggerSync = async () => {
        setSyncing(true);
        try {
            const r = await API.post('/admin/sync');
            flash(`Synced ${r.data.synced} fixtures`);
            loadStats();
        } catch { flash('Sync failed'); }
        setSyncing(false);
    };

    const triggerHistoricalSync = async () => {
        setHistoricalSyncing(true);
        try {
            await API.post('/admin/historical-sync');
            flash('Historical sync started! Check progress below.');
            pollHistoricalStatus();
        } catch { flash('Historical sync failed to start'); setHistoricalSyncing(false); }
    };

    const pollHistoricalStatus = () => {
        const interval = setInterval(async () => {
            try {
                const r = await API.get('/admin/historical-sync/status');
                setHistoricalStatus(r.data);
                if (!r.data.running) {
                    clearInterval(interval);
                    setHistoricalSyncing(false);
                    loadStats();
                    flash(`Historical sync complete! ${r.data.total_fixtures} fixtures`);
                }
            } catch { clearInterval(interval); setHistoricalSyncing(false); }
        }, 3000);
    };

    const triggerAiTraining = async () => {
        setAiTraining(true);
        try {
            await API.post('/admin/train-ai');
            flash('AI training started in background');
            setTimeout(() => { loadStats(); setAiTraining(false); }, 10000);
        } catch { flash('AI training failed'); setAiTraining(false); }
    };

    const saveUser = async () => {
        try {
            if (editUser) {
                const payload = { ...userForm };
                if (!payload.password) delete payload.password;
                await API.put(`/admin/users/${editUser.id}`, payload);
                flash('User updated');
            } else {
                await API.post('/admin/users', userForm);
                flash('User created');
            }
            setShowUserModal(false);
            setEditUser(null);
            setUserForm({ name: '', email: '', password: '', role: 'user' });
            API.get('/admin/users').then(r => setUsers(r.data.users || []));
        } catch (e) {
            flash(e.response?.data?.detail || 'Error saving user');
        }
    };

    const deleteUser = async (uid) => {
        if (!window.confirm('Delete this user?')) return;
        try {
            await API.delete(`/admin/users/${uid}`);
            flash('User deleted');
            setUsers(users.filter(u => u.id !== uid));
        } catch (e) { flash(e.response?.data?.detail || 'Error'); }
    };

    const openEditUser = (u) => {
        setEditUser(u);
        setUserForm({ name: u.name, email: u.email, password: '', role: u.role });
        setShowUserModal(true);
    };

    const saveApiConfig = async () => {
        try {
            const payload = {};
            if (apiKeyForm.sportmonks_api_token) payload.sportmonks_api_token = apiKeyForm.sportmonks_api_token;
            if (apiKeyForm.emergent_llm_key) payload.emergent_llm_key = apiKeyForm.emergent_llm_key;
            await API.put('/admin/api-config', payload);
            flash('API keys updated');
            setShowApiKeyModal(false);
            setApiKeyForm({ sportmonks_api_token: '', emergent_llm_key: '' });
            API.get('/admin/api-config').then(r => setApiConfig(r.data));
        } catch (e) { flash(e.response?.data?.detail || 'Error'); }
    };

    if (loading || !stats) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="admin-panel">
            {/* Header */}
            <div className="glass-card p-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5 text-red-400" />
                        <h2 className="heading-primary text-base md:text-xl text-white">ADMIN CONTROL PANEL</h2>
                        <div className="badge-ai">SYSTEM MONITOR</div>
                    </div>
                    <Button data-testid="sync-btn" onClick={triggerSync} disabled={syncing} size="sm"
                        className="bg-cyan-600 hover:bg-cyan-500 text-white gap-2">
                        <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                        {syncing ? 'Syncing...' : 'Force Sync'}
                    </Button>
                </div>
            </div>

            {/* Action Message */}
            {actionMsg && (
                <div className="glass-card p-3 border-l-4 border-l-cyan-400 animate-fadeInUp">
                    <span className="text-sm text-cyan-400">{actionMsg}</span>
                </div>
            )}

            {/* Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-1">
                <Tab active={tab === 'overview'} onClick={() => setTab('overview')} icon={Activity} label="Overview" testId="tab-overview" />
                <Tab active={tab === 'sync'} onClick={() => setTab('sync')} icon={RefreshCw} label="Sync Jobs" testId="tab-sync" />
                <Tab active={tab === 'api'} onClick={() => setTab('api')} icon={Key} label="API Config" testId="tab-api" />
                <Tab active={tab === 'connectivity'} onClick={() => setTab('connectivity')} icon={Wifi} label="Connectivity" testId="tab-connectivity" />
                <Tab active={tab === 'users'} onClick={() => setTab('users')} icon={Users} label="Users" testId="tab-users" />
                <Tab active={tab === 'readiness'} onClick={() => setTab('readiness')} icon={Target} label="Match Readiness" testId="tab-readiness" />
                <Tab active={tab === 'settings'} onClick={() => setTab('settings')} icon={Settings} label="Settings" testId="tab-settings" />
            </div>

            {/* === OVERVIEW TAB === */}
            {tab === 'overview' && (
                <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="glass-card p-5 flex items-center gap-3">
                            <Users className="w-5 h-5 text-cyan-400" />
                            <div><div className="tech-mono text-[10px] text-white/30">USERS</div><div className="heading-primary text-2xl text-white">{stats.users}</div></div>
                        </div>
                        <div className="glass-card p-5 flex items-center gap-3">
                            <Target className="w-5 h-5 text-purple-400" />
                            <div>
                                <div className="tech-mono text-[10px] text-white/30">PREDICTIONS (2026)</div>
                                <div className="heading-primary text-2xl text-white">{stats.predictions_2026 || 0}</div>
                                <div className="text-[9px] text-white/20">Total: {stats.predictions}</div>
                            </div>
                        </div>
                        <div className="glass-card p-5 flex items-center gap-3">
                            <Database className="w-5 h-5 text-green-400" />
                            <div><div className="tech-mono text-[10px] text-white/30">FIXTURES SYNCED</div><div className="heading-primary text-2xl text-white">{stats.sportmonks_fixtures_synced}</div></div>
                        </div>
                        <div className="glass-card p-5 flex items-center gap-3">
                            <Zap className="w-5 h-5 text-yellow-400" />
                            <div><div className="tech-mono text-[10px] text-white/30">API CACHE</div><div className="heading-primary text-2xl text-white">{stats.api_cache_entries}</div></div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="glass-card p-6">
                            <h3 className="heading-primary text-sm text-white mb-4">SERVICE STATUS</h3>
                            <div className="space-y-3">
                                {[
                                    { name: 'Sportmonks API', status: stats.sportmonks_status, icon: Wifi, color: 'text-cyan-400' },
                                    { name: `AI Model (${stats.ai_model})`, status: stats.ai_status, icon: Brain, color: 'text-purple-400' },
                                    { name: stats.database, status: 'connected', icon: Database, color: 'text-green-400' },
                                ].map((svc, i) => (
                                    <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <svc.icon className={`w-4 h-4 ${svc.color}`} />
                                            <span className="text-sm text-white">{svc.name}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <StatusDot status={svc.status === 'active' || svc.status === 'connected' ? 'connected' : 'error'} />
                                            <span className="text-xs font-bold text-white/60">{svc.status?.toUpperCase()}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {stats.sportmonks_by_season && (
                                <div className="mt-4 p-3 bg-white/5 rounded-lg">
                                    <div className="text-xs text-white/40 mb-2">SEASON BREAKDOWN</div>
                                    {Object.entries(stats.sportmonks_by_season).map(([k, v]) => (
                                        <div key={k} className="flex justify-between text-sm py-1">
                                            <span className="text-white/60">{k}</span>
                                            <span className="text-white font-bold">{v} fixtures</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="glass-card p-6">
                            <div className="flex items-center gap-2 mb-4">
                                <Activity className="w-4 h-4 text-green-400" />
                                <h3 className="heading-primary text-sm text-white">MODEL ACCURACY TREND</h3>
                                {stats.accuracy_trend?.length > 0 && (
                                    <span className="text-[10px] px-2 py-0.5 rounded bg-green-500/20 text-green-400 tech-mono ml-auto">
                                        {stats.accuracy_trend[stats.accuracy_trend.length - 1]?.accuracy}% CURRENT
                                    </span>
                                )}
                            </div>
                            {stats.accuracy_trend?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={220}>
                                    <LineChart data={stats.accuracy_trend}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                                        <XAxis dataKey="match" tick={{ fill: '#666', fontSize: 9 }} />
                                        <YAxis domain={[0, 100]} tick={{ fill: '#666', fontSize: 10 }} />
                                        <Tooltip contentStyle={{ background: '#000', border: '1px solid #333', borderRadius: 8 }} labelStyle={{ color: '#fff' }}
                                            formatter={(v) => [`${v}%`, 'Accuracy']} />
                                        <Line type="monotone" dataKey="accuracy" stroke="#39FF14" strokeWidth={2} dot={{ fill: '#39FF14', r: 4 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="text-center py-8">
                                    <Activity className="w-8 h-8 text-white/10 mx-auto mb-2" />
                                    <div className="text-sm text-white/20">Accuracy trend will populate after matches begin</div>
                                    <div className="text-[10px] text-white/10 mt-1">Model retrains after each completed match</div>
                                </div>
                            )}
                        </div>

                        {/* Additional Stats */}
                        <div className="glass-card p-6 md:col-span-2">
                            <h3 className="heading-primary text-sm text-white mb-4">SYSTEM OVERVIEW</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div className="bg-white/5 rounded-lg p-3 text-center">
                                    <div className="tech-mono text-[10px] text-white/30">2026 SQUAD</div>
                                    <div className="text-xl font-bold text-cyan-400">{stats.squad_players_2026 || 0}</div>
                                    <div className="text-[10px] text-white/15">Players synced</div>
                                </div>
                                <div className="bg-white/5 rounded-lg p-3 text-center">
                                    <div className="tech-mono text-[10px] text-white/30">INTEL REPORTS</div>
                                    <div className="text-xl font-bold text-purple-400">{stats.intelligence_reports || 0}</div>
                                    <div className="text-[10px] text-white/15">Generated</div>
                                </div>
                                <div className="bg-white/5 rounded-lg p-3 text-center">
                                    <div className="tech-mono text-[10px] text-white/30">INTEL CYCLES</div>
                                    <div className="text-xl font-bold text-emerald-400">{stats.intelligence_cycles || 0}</div>
                                    <div className="text-[10px] text-white/15">Completed</div>
                                </div>
                                <div className="bg-white/5 rounded-lg p-3 text-center">
                                    <div className="tech-mono text-[10px] text-white/30">PREDICTIONS 2026</div>
                                    <div className="text-xl font-bold text-amber-400">{stats.predictions_2026 || 0}</div>
                                    <div className="text-[10px] text-white/15">This season</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* === SYNC JOBS TAB === */}
            {tab === 'sync' && (
                <div className="space-y-4">
                    <div className="glass-card p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="heading-primary text-sm text-white">BACKGROUND SYNC JOBS</h3>
                            <Button data-testid="sync-now-btn" onClick={triggerSync} disabled={syncing} size="sm" variant="outline" className="border-white/10 text-white gap-2">
                                <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} />
                                {syncing ? 'Running...' : 'Run Now'}
                            </Button>
                        </div>
                        <div className="space-y-3">
                            {stats.sync_jobs?.map((job, i) => (
                                <div key={i} className="flex items-center justify-between p-4 bg-white/5 rounded-lg" data-testid={`sync-job-${i}`}>
                                    <div className="flex items-center gap-3">
                                        <StatusDot status={job.status} />
                                        <div>
                                            <div className="text-sm text-white font-medium">{job.name}</div>
                                            <div className="tech-mono text-[10px] text-white/30">Interval: {job.interval}</div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className={`text-xs font-bold ${job.status === 'active' ? 'text-green-400' : job.status === 'running' ? 'text-blue-400' : 'text-yellow-400'}`}>
                                            {job.status?.toUpperCase()}
                                        </div>
                                        <div className="tech-mono text-[10px] text-white/20">
                                            {job.last_run && job.last_run !== 'never' ? new Date(job.last_run).toLocaleString() : 'Never'}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    {stats.sync_state && (
                        <div className="glass-card p-6">
                            <h3 className="heading-primary text-sm text-white mb-4">SYNC STATE</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="p-3 bg-white/5 rounded-lg">
                                    <div className="tech-mono text-[10px] text-white/30">TOTAL SYNCED</div>
                                    <div className="text-xl text-white font-bold">{stats.sync_state.fixtures_synced}</div>
                                </div>
                                <div className="p-3 bg-white/5 rounded-lg">
                                    <div className="tech-mono text-[10px] text-white/30">AUTO PREDICTIONS</div>
                                    <div className="text-xl text-white font-bold">{stats.sync_state.auto_predictions_count || 0}</div>
                                </div>
                                <div className="p-3 bg-white/5 rounded-lg">
                                    <div className="tech-mono text-[10px] text-white/30">LAST SYNC</div>
                                    <div className="text-xs text-white/60">{stats.sync_state.last_fixtures_sync ? new Date(stats.sync_state.last_fixtures_sync).toLocaleString() : 'Never'}</div>
                                </div>
                                <div className="p-3 bg-white/5 rounded-lg">
                                    <div className="tech-mono text-[10px] text-white/30">ERRORS</div>
                                    <div className={`text-xl font-bold ${stats.sync_state.errors?.length ? 'text-red-400' : 'text-green-400'}`}>
                                        {stats.sync_state.errors?.length || 0}
                                    </div>
                                </div>
                            </div>
                            {stats.sync_state.errors?.length > 0 && (
                                <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                                    <div className="text-xs text-red-400 font-bold mb-1">RECENT ERRORS</div>
                                    {stats.sync_state.errors.map((e, i) => <div key={i} className="text-xs text-red-300/70">{e}</div>)}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Historical Sync Section */}
                    <div className="glass-card p-6" data-testid="historical-sync-section">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="heading-primary text-sm text-white">HISTORICAL DATA SYNC</h3>
                                <p className="text-xs text-white/30 mt-1">Fetch all IPL matches from 2013-2026 (14 seasons) from Sportmonks</p>
                            </div>
                            <Button data-testid="historical-sync-btn" onClick={triggerHistoricalSync}
                                disabled={historicalSyncing} size="sm"
                                className="bg-amber-600 hover:bg-amber-500 text-white gap-2">
                                <Database className={`w-4 h-4 ${historicalSyncing ? 'animate-pulse' : ''}`} />
                                {historicalSyncing ? 'Syncing...' : 'Sync All Seasons'}
                            </Button>
                        </div>
                        {(historicalStatus || stats.historical_sync_state) && (() => {
                            const hs = historicalStatus || stats.historical_sync_state;
                            return (
                                <div className="space-y-3">
                                    {hs.running && (
                                        <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                                            <div className="flex items-center gap-2 mb-2">
                                                <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                                                <span className="text-xs text-blue-400 font-bold">{hs.progress}</span>
                                            </div>
                                            <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                                                <div className="h-full bg-blue-400 rounded-full transition-all" style={{ width: `${(hs.seasons_done / hs.total_seasons) * 100}%` }} />
                                            </div>
                                            <div className="text-[10px] text-white/30 mt-1">{hs.seasons_done}/{hs.total_seasons} seasons</div>
                                        </div>
                                    )}
                                    {!hs.running && hs.total_fixtures > 0 && (
                                        <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                                            <div className="text-xs text-green-400 font-bold">{hs.progress || `${hs.total_fixtures} fixtures synced`}</div>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                    </div>

                    {/* AI Training Section */}
                    <div className="glass-card p-6" data-testid="ai-training-section">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="heading-primary text-sm text-white">AI MODEL TRAINING</h3>
                                <p className="text-xs text-white/30 mt-1">Compute real team stats, venue intelligence, and H2H records from historical data</p>
                            </div>
                            <Button data-testid="train-ai-btn" onClick={triggerAiTraining}
                                disabled={aiTraining} size="sm"
                                className="bg-purple-600 hover:bg-purple-500 text-white gap-2">
                                <Brain className={`w-4 h-4 ${aiTraining ? 'animate-spin' : ''}`} />
                                {aiTraining ? 'Training...' : 'Re-Train AI'}
                            </Button>
                        </div>
                        {stats.ai_training && (
                            <div className="grid grid-cols-3 gap-3">
                                <div className="p-3 bg-white/5 rounded-lg text-center">
                                    <div className="text-xl text-purple-400 font-bold">{stats.ai_training.trained_teams}</div>
                                    <div className="tech-mono text-[10px] text-white/30">TEAMS</div>
                                </div>
                                <div className="p-3 bg-white/5 rounded-lg text-center">
                                    <div className="text-xl text-cyan-400 font-bold">{stats.ai_training.trained_venues}</div>
                                    <div className="tech-mono text-[10px] text-white/30">VENUES</div>
                                </div>
                                <div className="p-3 bg-white/5 rounded-lg text-center">
                                    <div className="text-xl text-yellow-400 font-bold">{stats.ai_training.h2h_pairs}</div>
                                    <div className="tech-mono text-[10px] text-white/30">H2H PAIRS</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* === API CONFIG TAB === */}
            {tab === 'api' && (
                <div className="space-y-4">
                    <div className="glass-card p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="heading-primary text-sm text-white">API CONFIGURATION</h3>
                            <Button data-testid="edit-api-keys-btn" onClick={() => setShowApiKeyModal(true)} size="sm" variant="outline" className="border-white/10 text-white gap-2">
                                <Key className="w-3 h-3" /> Update Keys
                            </Button>
                        </div>
                        {apiConfig && (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg" data-testid="sportmonks-config">
                                    <div className="flex items-center gap-3">
                                        <Wifi className="w-4 h-4 text-cyan-400" />
                                        <div>
                                            <div className="text-sm text-white">Sportmonks API Token</div>
                                            <div className="tech-mono text-xs text-white/30">{apiConfig.sportmonks_base_url}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <code className="text-xs text-white/50 bg-black/30 px-2 py-1 rounded">{apiConfig.sportmonks_api_token}</code>
                                        {apiConfig.sportmonks_api_token !== 'not_set' ? <CheckCircle className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                                    </div>
                                </div>
                                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg" data-testid="emergent-config">
                                    <div className="flex items-center gap-3">
                                        <Brain className="w-4 h-4 text-purple-400" />
                                        <div>
                                            <div className="text-sm text-white">Emergent LLM Key</div>
                                            <div className="tech-mono text-xs text-white/30">GPT-5.2 + AI Report Generation</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <code className="text-xs text-white/50 bg-black/30 px-2 py-1 rounded">{apiConfig.emergent_llm_key}</code>
                                        {apiConfig.emergent_llm_key !== 'not_set' ? <CheckCircle className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                                    </div>
                                </div>
                                <div className="p-4 bg-white/5 rounded-lg">
                                    <div className="text-xs text-white/40 mb-2">ADDITIONAL CONFIG</div>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        <span className="text-white/40">JWT Secret</span><span className="text-white">{apiConfig.jwt_secret}</span>
                                        <span className="text-white/40">Team Mappings</span><span className="text-white">{apiConfig.sportmonks_team_map_count} teams</span>
                                        <span className="text-white/40">Seasons Config</span><span className="text-white">{Object.entries(apiConfig.sportmonks_seasons || {}).map(([k, v]) => `${k}: ${v}`).join(', ')}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    {apiLogs.length > 0 && (
                        <div className="glass-card p-6">
                            <h3 className="heading-primary text-sm text-white mb-4">RECENT API CALLS ({apiLogs.length})</h3>
                            <div className="space-y-2 max-h-80 overflow-y-auto">
                                {apiLogs.map((log, i) => (
                                    <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-lg text-xs">
                                        <div className="flex items-center gap-2">
                                            <Server className="w-3 h-3 text-cyan-400" />
                                            <span className="text-white/80 font-mono">{log.endpoint}</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="text-green-400">{log.status}</span>
                                            <span className="text-white/30">{log.cached_at ? new Date(log.cached_at).toLocaleString() : '-'}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* === CONNECTIVITY TAB === */}
            {tab === 'connectivity' && (
                <div className="glass-card p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="heading-primary text-sm text-white">API CONNECTIVITY TEST</h3>
                        <Button data-testid="retest-btn" onClick={() => { setConnectivity(null); API.get('/admin/connectivity').then(r => setConnectivity(r.data)); }} size="sm" variant="outline" className="border-white/10 text-white gap-2">
                            <RefreshCw className="w-3 h-3" /> Retest
                        </Button>
                    </div>
                    {!connectivity ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                            <span className="text-white/40 ml-3">Testing connections...</span>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {Object.entries(connectivity).map(([name, result]) => (
                                <div key={name} className={`p-5 rounded-lg border ${result.status === 'connected' ? 'bg-green-500/5 border-green-500/20' : 'bg-red-500/5 border-red-500/20'}`} data-testid={`connectivity-${name}`}>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            {result.status === 'connected' ? <CheckCircle className="w-5 h-5 text-green-400" /> : <XCircle className="w-5 h-5 text-red-400" />}
                                            <div>
                                                <div className="text-white font-medium capitalize">{name.replace('_', ' ')}</div>
                                                <div className="text-xs text-white/30">{name === 'sportmonks' ? 'cricket.sportmonks.com/api/v2.0' : 'localhost:27017'}</div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className={`text-sm font-bold ${result.status === 'connected' ? 'text-green-400' : 'text-red-400'}`}>
                                                {result.status?.toUpperCase()}
                                            </div>
                                            {result.latency_ms && <div className="text-xs text-white/30">{result.latency_ms}ms</div>}
                                            {result.code && <div className="text-xs text-white/30">HTTP {result.code}</div>}
                                        </div>
                                    </div>
                                    {result.error && <div className="mt-2 text-xs text-red-300/70 bg-red-500/10 p-2 rounded">{result.error}</div>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* === USERS TAB === */}
            {tab === 'users' && (
                <div className="space-y-4">
                    <div className="glass-card p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="heading-primary text-sm text-white">USER MANAGEMENT</h3>
                            <Button data-testid="add-user-btn" onClick={() => { setEditUser(null); setUserForm({ name: '', email: '', password: '', role: 'user' }); setShowUserModal(true); }} size="sm" className="bg-cyan-600 hover:bg-cyan-500 text-white gap-2">
                                <Plus className="w-3 h-3" /> Add User
                            </Button>
                        </div>
                        <div className="space-y-2">
                            {users.map(u => (
                                <div key={u.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg" data-testid={`user-row-${u.id}`}>
                                    <div className="flex items-center gap-3">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${u.role === 'admin' ? 'bg-red-500/20 text-red-400' : 'bg-cyan-500/20 text-cyan-400'}`}>
                                            {u.name?.charAt(0)?.toUpperCase() || '?'}
                                        </div>
                                        <div>
                                            <div className="text-sm text-white">{u.name}</div>
                                            <div className="text-xs text-white/40">{u.email}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className={`text-[10px] font-bold px-2 py-1 rounded ${u.role === 'admin' ? 'bg-red-500/10 text-red-400' : 'bg-cyan-500/10 text-cyan-400'}`}>
                                            {u.role?.toUpperCase()}
                                        </span>
                                        <span className="text-[10px] text-white/20">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ''}</span>
                                        <button data-testid={`edit-user-${u.id}`} onClick={() => openEditUser(u)} className="p-1.5 hover:bg-white/10 rounded transition-colors">
                                            <Pencil className="w-3.5 h-3.5 text-white/40 hover:text-white" />
                                        </button>
                                        <button data-testid={`delete-user-${u.id}`} onClick={() => deleteUser(u.id)} className="p-1.5 hover:bg-red-500/10 rounded transition-colors">
                                            <Trash2 className="w-3.5 h-3.5 text-white/40 hover:text-red-400" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* === SETTINGS TAB === */}
            {tab === 'settings' && <SettingsTab flash={flash} />}

            {/* === MATCH READINESS TAB === */}
            {tab === 'readiness' && (
                <div className="space-y-4" data-testid="match-readiness-tab">
                    {/* Action Buttons */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="glass-card p-5 border border-red-500/20">
                            <div className="flex items-center gap-2 mb-3">
                                <Zap className="w-4 h-4 text-red-400" />
                                <h3 className="text-sm text-white font-bold">Push Live Data</h3>
                            </div>
                            <p className="text-xs text-white/30 mb-4">Send live match data + Telegram notification for current match.</p>
                            <Button data-testid="push-live-btn" onClick={async () => {
                                setPushingLive(true);
                                try {
                                    const r = await API.post('/admin/push-live-data');
                                    flash(r.data.status === 'pushed' ? `Live data pushed: ${r.data.teams}` : 'No live match right now');
                                    setMatchReadiness(r.data);
                                } catch { flash('Push failed'); }
                                setPushingLive(false);
                            }} disabled={pushingLive} size="sm" className="w-full bg-red-600 hover:bg-red-500 text-white gap-2">
                                <Activity className={`w-3 h-3 ${pushingLive ? 'animate-pulse' : ''}`} />
                                {pushingLive ? 'Pushing...' : 'Push Live Match'}
                            </Button>
                        </div>

                        <div className="glass-card p-5 border border-cyan-500/20">
                            <div className="flex items-center gap-2 mb-3">
                                <Brain className="w-4 h-4 text-cyan-400" />
                                <h3 className="text-sm text-white font-bold">Batch Predictions</h3>
                            </div>
                            <p className="text-xs text-white/30 mb-4">Generate AI predictions for all upcoming IPL 2026 matches.</p>
                            <Button data-testid="batch-predict-btn" onClick={async () => {
                                setBatchRunning(true);
                                try {
                                    const r = await API.post('/admin/batch-predictions');
                                    flash(`Generated ${r.data.generated} predictions (${r.data.existing} existing)`);
                                    API.get('/admin/upcoming-predictions').then(r2 => setUpcomingPreds(r2.data)).catch(() => {});
                                } catch { flash('Batch prediction failed'); }
                                setBatchRunning(false);
                            }} disabled={batchRunning} size="sm" className="w-full bg-cyan-600 hover:bg-cyan-500 text-white gap-2">
                                <Brain className={`w-3 h-3 ${batchRunning ? 'animate-spin' : ''}`} />
                                {batchRunning ? 'Generating...' : 'Generate All Predictions'}
                            </Button>
                        </div>

                        <div className="glass-card p-5 border border-green-500/20">
                            <div className="flex items-center gap-2 mb-3">
                                <Target className="w-4 h-4 text-green-400" />
                                <h3 className="text-sm text-white font-bold">Full Setup</h3>
                            </div>
                            <p className="text-xs text-white/30 mb-4">Predictions + scraper schedules + Telegram alerts for all matches.</p>
                            <Button data-testid="full-readiness-btn" onClick={async () => {
                                setSetupRunning(true);
                                try {
                                    const r = await API.post('/admin/match-readiness');
                                    flash(`Ready! ${r.data.predictions?.generated || 0} new predictions, ${r.data.scheduled_scraping?.length || 0} scrapers scheduled`);
                                    API.get('/admin/upcoming-predictions').then(r2 => setUpcomingPreds(r2.data)).catch(() => {});
                                } catch { flash('Setup failed'); }
                                setSetupRunning(false);
                            }} disabled={setupRunning} size="sm" className="w-full bg-green-600 hover:bg-green-500 text-white gap-2">
                                <Target className={`w-3 h-3 ${setupRunning ? 'animate-spin' : ''}`} />
                                {setupRunning ? 'Setting up...' : 'Full Match Readiness'}
                            </Button>
                        </div>
                    </div>

                    {/* Readiness Result */}
                    {matchReadiness && (
                        <div className="glass-card p-4 border-l-4 border-l-green-500">
                            <div className="text-xs text-white/40">Last Action Result</div>
                            <pre className="text-xs text-green-400 mt-1">{JSON.stringify(matchReadiness, null, 2)}</pre>
                        </div>
                    )}

                    {/* Upcoming Predictions Table */}
                    {upcomingPreds && (
                        <div className="glass-card p-5">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                    <Brain className="w-4 h-4 text-cyan-400" />
                                    <h3 className="heading-primary text-sm text-white">ALL UPCOMING PREDICTIONS</h3>
                                </div>
                                <span className="text-[10px] px-2 py-0.5 bg-cyan-500/20 text-cyan-400 rounded tech-mono">{upcomingPreds.total} MATCHES</span>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs" data-testid="predictions-table">
                                    <thead>
                                        <tr className="text-white/30 tech-mono text-[9px] border-b border-white/10">
                                            <th className="text-left py-2 px-2">DATE</th>
                                            <th className="text-left py-2 px-2">MATCH</th>
                                            <th className="text-left py-2 px-2">VENUE</th>
                                            <th className="text-right py-2 px-2">HOME %</th>
                                            <th className="text-right py-2 px-2">AWAY %</th>
                                            <th className="text-left py-2 px-2">WINNER</th>
                                            <th className="text-right py-2 px-2">CONF</th>
                                            <th className="text-left py-2 px-2">IMPACT</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {upcomingPreds.predictions.map((p, i) => (
                                            <tr key={i} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                                                <td className="py-2 px-2 text-white/40">{p.date?.slice(0, 10)}</td>
                                                <td className="py-2 px-2 text-white font-medium">{p.teams}</td>
                                                <td className="py-2 px-2 text-white/30">{p.venue?.split(' ')[0] || '-'}</td>
                                                <td className="py-2 px-2 text-right text-cyan-400">{p.team_a_pct}%</td>
                                                <td className="py-2 px-2 text-right text-pink-400">{p.team_b_pct}%</td>
                                                <td className="py-2 px-2 text-green-400 font-bold">{p.predicted_winner}</td>
                                                <td className="py-2 px-2 text-right text-amber-400">{Math.round(p.confidence * 100)}%</td>
                                                <td className="py-2 px-2 text-white/20 text-[9px]">{p.impact_player?.team_a_best || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* === USER MODAL === */}
            {showUserModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="user-modal">
                    <div className="glass-card p-6 w-full max-w-md mx-4">
                        <h3 className="heading-primary text-sm text-white mb-4">{editUser ? 'EDIT USER' : 'ADD NEW USER'}</h3>
                        <div className="space-y-3">
                            <Input data-testid="user-name-input" placeholder="Full Name" value={userForm.name} onChange={e => setUserForm(p => ({ ...p, name: e.target.value }))} className="bg-black/50 border-white/10 text-white" />
                            <Input data-testid="user-email-input" placeholder="Email" type="email" value={userForm.email} onChange={e => setUserForm(p => ({ ...p, email: e.target.value }))} className="bg-black/50 border-white/10 text-white" />
                            <div className="relative">
                                <Input data-testid="user-password-input" placeholder={editUser ? 'New password (leave blank to keep)' : 'Password'} type={showPw.modal ? 'text' : 'password'} value={userForm.password} onChange={e => setUserForm(p => ({ ...p, password: e.target.value }))} className="bg-black/50 border-white/10 text-white pr-10" />
                                <button onClick={() => setShowPw(p => ({ ...p, modal: !p.modal }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30">
                                    {showPw.modal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                            <Select value={userForm.role} onValueChange={v => setUserForm(p => ({ ...p, role: v }))}>
                                <SelectTrigger data-testid="user-role-select" className="bg-black/50 border-white/10 text-white">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0a0a0a] border-white/10">
                                    <SelectItem value="user" className="text-white">User</SelectItem>
                                    <SelectItem value="admin" className="text-white">Admin</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex gap-3 mt-5">
                            <Button data-testid="save-user-btn" onClick={saveUser} className="flex-1 bg-cyan-600 hover:bg-cyan-500 text-white">
                                {editUser ? 'Update' : 'Create'}
                            </Button>
                            <Button onClick={() => { setShowUserModal(false); setEditUser(null); }} variant="outline" className="border-white/10 text-white">Cancel</Button>
                        </div>
                    </div>
                </div>
            )}

            {/* === API KEY MODAL === */}
            {showApiKeyModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="api-key-modal">
                    <div className="glass-card p-6 w-full max-w-md mx-4">
                        <div className="flex items-center gap-2 mb-4">
                            <AlertTriangle className="w-4 h-4 text-yellow-400" />
                            <h3 className="heading-primary text-sm text-white">UPDATE API KEYS</h3>
                        </div>
                        <p className="text-xs text-white/40 mb-4">Leave blank to keep current value. Changes take effect immediately (runtime only).</p>
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs text-white/50 mb-1 block">Sportmonks API Token</label>
                                <Input data-testid="sportmonks-key-input" placeholder="Enter new token..." value={apiKeyForm.sportmonks_api_token} onChange={e => setApiKeyForm(p => ({ ...p, sportmonks_api_token: e.target.value }))} className="bg-black/50 border-white/10 text-white font-mono text-xs" />
                            </div>
                            <div>
                                <label className="text-xs text-white/50 mb-1 block">Emergent LLM Key</label>
                                <Input data-testid="emergent-key-input" placeholder="Enter new key..." value={apiKeyForm.emergent_llm_key} onChange={e => setApiKeyForm(p => ({ ...p, emergent_llm_key: e.target.value }))} className="bg-black/50 border-white/10 text-white font-mono text-xs" />
                            </div>
                        </div>
                        <div className="flex gap-3 mt-5">
                            <Button data-testid="save-api-keys-btn" onClick={saveApiConfig} className="flex-1 bg-cyan-600 hover:bg-cyan-500 text-white">Save</Button>
                            <Button onClick={() => setShowApiKeyModal(false)} variant="outline" className="border-white/10 text-white">Cancel</Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function SettingsTab({ flash }) {
    const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
    const [showPw, setShowPw] = useState({});
    const [saving, setSaving] = useState(false);

    const changePw = async () => {
        if (pwForm.new_password !== pwForm.confirm) { flash('Passwords do not match'); return; }
        if (pwForm.new_password.length < 6) { flash('Password must be at least 6 characters'); return; }
        setSaving(true);
        try {
            await API.put('/auth/change-password', { current_password: pwForm.current_password, new_password: pwForm.new_password });
            flash('Password changed successfully');
            setPwForm({ current_password: '', new_password: '', confirm: '' });
        } catch (e) { flash(e.response?.data?.detail || 'Error changing password'); }
        setSaving(false);
    };

    return (
        <div className="glass-card p-6 max-w-lg">
            <h3 className="heading-primary text-sm text-white mb-4">CHANGE YOUR PASSWORD</h3>
            <div className="space-y-3">
                <div className="relative">
                    <Input data-testid="current-pw-input" placeholder="Current Password" type={showPw.cur ? 'text' : 'password'} value={pwForm.current_password} onChange={e => setPwForm(p => ({ ...p, current_password: e.target.value }))} className="bg-black/50 border-white/10 text-white pr-10" />
                    <button onClick={() => setShowPw(p => ({ ...p, cur: !p.cur }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30">
                        {showPw.cur ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                </div>
                <div className="relative">
                    <Input data-testid="new-pw-input" placeholder="New Password" type={showPw.new_ ? 'text' : 'password'} value={pwForm.new_password} onChange={e => setPwForm(p => ({ ...p, new_password: e.target.value }))} className="bg-black/50 border-white/10 text-white pr-10" />
                    <button onClick={() => setShowPw(p => ({ ...p, new_: !p.new_ }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30">
                        {showPw.new_ ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                </div>
                <Input data-testid="confirm-pw-input" placeholder="Confirm New Password" type="password" value={pwForm.confirm} onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))} className="bg-black/50 border-white/10 text-white" />
            </div>
            <Button data-testid="change-pw-btn" onClick={changePw} disabled={saving} className="mt-4 bg-cyan-600 hover:bg-cyan-500 text-white w-full">
                {saving ? 'Changing...' : 'Change Password'}
            </Button>
        </div>
    );
}
